<script setup lang="ts">
  import type { SwitchRootEmits, SwitchRootProps } from "reka-ui";
  import type { HTMLAttributes } from "vue";
  import { reactiveOmit } from "@vueuse/core";
  import { SwitchRoot, SwitchThumb, useForwardPropsEmits } from "reka-ui";
  import { cn } from "@/lib/utils";

  const props = withDefaults(defineProps<SwitchRootProps & { class?: HTMLAttributes["class"]; size?: "default" | "sm" }>(), {
    class: undefined,
    size: "default",
  });

  const emits = defineEmits<SwitchRootEmits>();

  const delegatedProps = reactiveOmit(props, "class", "size");

  const forwarded = useForwardPropsEmits(delegatedProps, emits);
</script>

<template>
  <SwitchRoot
    v-slot="slotProps"
    data-slot="switch"
    v-bind="forwarded"
    :class="
      cn(
        'peer data-[state=checked]:bg-primary data-[state=unchecked]:bg-input focus-visible:border-ring focus-visible:ring-ring/10 dark:data-[state=unchecked]:bg-input/80 inline-flex shrink-0 items-center rounded-full border border-transparent shadow-xs transition-all outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50',
        props.size === 'sm' ? 'h-3.5 w-6' : 'h-[1.15rem] w-8',
        props.class,
      )
    ">
    <SwitchThumb
      data-slot="switch-thumb"
      :class="
        cn(
          'bg-background dark:data-[state=unchecked]:bg-foreground dark:data-[state=checked]:bg-primary-foreground pointer-events-none block rounded-full ring-0 transition-transform data-[state=checked]:translate-x-[calc(100%-2px)] data-[state=unchecked]:translate-x-0',
          props.size === 'sm' ? 'size-3' : 'size-4',
        )
      ">
      <slot
        name="thumb"
        v-bind="slotProps" />
    </SwitchThumb>
  </SwitchRoot>
</template>
